'use client';

import type {
  SignupCustomField,
  SignupFieldEntry,
} from '@kit/auth/signup-custom-fields';
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { Switch } from '@kit/ui/switch';

/**
 * One tenant custom profile field as a form input (text, number, date, select,
 * boolean). Used by profile setup and the add-family-member form. Render it
 * inside a react-hook-form FormField.
 */
export function CustomFieldInput({
  field,
  onValueChange,
  value,
  disabled,
}: {
  field: SignupCustomField;
  value: SignupFieldEntry['value'];
  onValueChange: (value: SignupFieldEntry['value']) => void;
  disabled: boolean;
}) {
  switch (field.field_type) {
    case 'text':
      return (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.is_required && ' *'}
          </FormLabel>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormControl>
            <Input
              disabled={disabled}
              value={'text' in value ? value.text : ''}
              onChange={(e) => onValueChange({ text: e.target.value })}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      );

    case 'number':
      return (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.is_required && ' *'}
          </FormLabel>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormControl>
            <Input
              type="number"
              value={'number' in value ? value.number : ''}
              disabled={disabled}
              onChange={(e) => {
                onValueChange(
                  isNaN(e.target.valueAsNumber)
                    ? { text: '' }
                    : { number: e.target.valueAsNumber },
                );
              }}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      );

    case 'date':
      return (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.is_required && ' *'}
          </FormLabel>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormControl>
            <Input
              type="date"
              className="min-w-0 max-w-full"
              value={'date' in value ? value.date : ''}
              disabled={disabled}
              onChange={(e) => onValueChange({ date: e.target.value })}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      );

    case 'select':
      return (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.is_required && ' *'}
          </FormLabel>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <Select
            value={'select' in value ? value.select : ''}
            disabled={disabled}
            onValueChange={(value) => onValueChange({ select: value })}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {(field.options ?? []).map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      );

    case 'boolean':
      return (
        <FormItem className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <FormLabel className="cursor-pointer">
              {field.label}
              {field.is_required && ' *'}
            </FormLabel>
            {field.description && (
              <FormDescription className="text-xs">
                {field.description}
              </FormDescription>
            )}
          </div>
          <FormControl>
            <Switch
              checked={'boolean' in value ? value.boolean : false}
              disabled={disabled}
              onCheckedChange={(checked) => onValueChange({ boolean: checked })}
            />
          </FormControl>
        </FormItem>
      );

    default:
      return null;
  }
}
